import {
  AssetType,
  ListResource,
  OnboardingFulfilledBy,
  PaymentMethodType,
  RealTimeResource,
} from '@nemuru-eng/js-api-client';

import { APPLICATION_TYPE_MENU_ITEMS } from '@/components/shared/components/ApplicationList/ApplicationTypeSelect/constants/applicationTypeMenuItems';
import { MenuItemType } from '@/components/shared/components/ApplicationList/ApplicationTypeSelect/types/MenuItemType';
import { APPLICATION_TYPES_BY_ONBOARDING_FULFILLED, PRODUCTS_BY_ASSET_TYPE } from '@/config/config';
import { ApplicationType } from '@/config/types';

export const ApplicationTypeMap = {
  fromPaymentMethodType(paymentMethodType: PaymentMethodType): ApplicationType {
    return PaymentMethodTypeToApplicationTypeMap.get(paymentMethodType) ?? ApplicationType.CONSUMER_LOAN;
  },

  fromApplicationType(applicationType: ApplicationType): PaymentMethodType {
    const ReverseMap = new Map<ApplicationType, PaymentMethodType>(
      Array.from(PaymentMethodTypeToApplicationTypeMap.entries()).map(([key, value]) => [value, key]),
    );

    return ReverseMap.get(applicationType) ?? PaymentMethodType.CONSUMER_LOAN_INSTALMENTS;
  },

  fromAssetType(assetType?: AssetType): ApplicationType {
    return assetType ? PRODUCTS_BY_ASSET_TYPE[assetType] : ApplicationType.CONSUMER_LOAN;
  },

  toAssetTypes(applicationType?: ApplicationType): AssetType[] {
    if (!applicationType) return [];
    const allAssetTypes = Object.keys(PRODUCTS_BY_ASSET_TYPE) as AssetType[];
    return allAssetTypes.filter((assetType) => PRODUCTS_BY_ASSET_TYPE[assetType] === applicationType);
  },

  toOnboardingFulfilled(applicationType?: ApplicationType): OnboardingFulfilledBy[] {
    if (!applicationType) return [];
    const allOnboardingFulfilledBys = Object.keys(APPLICATION_TYPES_BY_ONBOARDING_FULFILLED) as OnboardingFulfilledBy[];
    return allOnboardingFulfilledBys.filter(
      (onboardingFulfilled) => APPLICATION_TYPES_BY_ONBOARDING_FULFILLED[onboardingFulfilled] === applicationType,
    );
  },

  recalculateOrderByOnboardingFulfilledBy(onboardingFulfilledBy: OnboardingFulfilledBy): ApplicationType {
    return APPLICATION_TYPES_BY_ONBOARDING_FULFILLED[onboardingFulfilledBy];
  },

  toMenuItem(applicationType: ApplicationType): MenuItemType {
    const menuItem = APPLICATION_TYPE_MENU_ITEMS.find((menuItem) => menuItem.value === applicationType);

    if (!menuItem) {
      throw new Error(`Application type ${applicationType} not found in menu items`);
    }

    return menuItem;
  },

  toListResource(applicationType?: ApplicationType): ListResource | undefined {
    switch (applicationType) {
      case ApplicationType.CONSUMER_LOAN:
        return RealTimeResource.CONSUMER_LOAN_LIST;
      case ApplicationType.CREDIT_LINE:
        return RealTimeResource.CREDIT_LINE_LIST;
      case ApplicationType.AUTO:
        return RealTimeResource.AUTO_LOAN_LIST;
      case ApplicationType.ORDER:
        return RealTimeResource.BNPL_LIST;
      case ApplicationType.PAY_BY_LINK:
        return RealTimeResource.PAY_BY_LINK_LIST;
      case ApplicationType.CARD:
        return RealTimeResource.CREDIT_CARD_LIST;
      case ApplicationType.CHECKOUT_SESSION:
        return RealTimeResource.CHECKOUT_SESSION_LIST;
      default:
        return undefined;
    }
  },
};

const PaymentMethodTypeToApplicationTypeMap = new Map<PaymentMethodType, ApplicationType>([
  [PaymentMethodType.AUTO_LOAN, ApplicationType.AUTO],
  [PaymentMethodType.CREDIT_LINE, ApplicationType.CREDIT_LINE],
  [PaymentMethodType.CARD_PAYMENT, ApplicationType.CARD_PAYMENT],
  [PaymentMethodType.CREDIT_CARD, ApplicationType.CARD],
  [PaymentMethodType.A2A_PAYMENT, ApplicationType.A2A_PAYMENT],
  [PaymentMethodType.CONSUMER_LOAN_INSTALMENTS, ApplicationType.CONSUMER_LOAN],
  [PaymentMethodType.CONSUMER_LOAN_SPLIT_PAY, ApplicationType.CONSUMER_LOAN],
  [PaymentMethodType.BNPL_INSTALMENTS, ApplicationType.ORDER],
  [PaymentMethodType.BNPL_SPLIT_PAY, ApplicationType.ORDER],
]);
